import React, { useState, MouseEvent, KeyboardEvent } from "react";
import { Box, Button, IconButton, Popover } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { StaticDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  addByView,
  CustomCalendarSelectorProps,
  getOpenTo,
  getViews,
} from "./CustomCalendarSelectorConstant";
import Flex from "../Flex";
import { CHART_DIMENSIONS, CHART_COLORS } from "../app/provider/patient/chart";
import { DEFAULT_FONTSIZE } from "../../utils/utils";
import { DATE_FORMAT } from "../../utils/date-time-utility";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const CustomCalendarSelector: React.FC<CustomCalendarSelectorProps> = ({
  value,
  view,
  maxDate = dayjs(),
  minDate,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Navigation helpers
  const prevDate = addByView(value, view, -1);
  const nextDate = addByView(value, view, 1);
  const canPrev = !minDate || prevDate.isSameOrAfter(minDate, view);
  const canNext = !maxDate || nextDate.isSameOrBefore(maxDate, view);

  // Label for main button (memoized)
  const display = React.useMemo(() => {
    if (view === "week") {
      const start = value.startOf("week");
      const end = value.endOf("week");
      return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    } else if (view === "month") {
      return value.format("MMMM YYYY");
    } else if (view === "year") {
      return value.format("YYYY");
    }
    return value.format(DATE_FORMAT);
  }, [value, view]);

  // Accessible interactive Buttons
  const interactiveIconButtons = (
    action: (e: MouseEvent | KeyboardEvent) => void,
    disabled: boolean,
    icon: React.ReactNode
  ) => (
    <IconButton
      disabled={disabled}
      style={{
        padding: 0,
        margin: 0,
      }}
      onClick={(e) => {
        e.stopPropagation();
        action(e);
      }}
    >
      {icon}
    </IconButton>
  );

  const handleButtonClick = (e: MouseEvent) => {
    setAnchorEl(e.currentTarget as HTMLElement);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <Flex
      alignItems="center"
      gap={1}
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: CHART_DIMENSIONS.controlHeight,
          display: "flex",
          alignItems: "center",
          padding: "0 8px",
        },
        "& .MuiOutlinedInput-input": {
          fontSize: DEFAULT_FONTSIZE.fontSize,
          padding: "0 !important",
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <Button
        variant="outlined"
        sx={{
          height: CHART_DIMENSIONS.controlHeight,
          borderColor: CHART_COLORS.border,
        }}
        startIcon={interactiveIconButtons(
          () => onChange(prevDate),
          !canPrev,
          <ArrowBackIosNewIcon fontSize="small" />
        )}
        endIcon={interactiveIconButtons(
          () => onChange(nextDate),
          !canNext,
          <ArrowForwardIosIcon fontSize="small" />
        )}
        onClick={handleButtonClick}
      >
        {display}
        <CalendarMonthIcon sx={{ ml: 1 }} fontSize="small" />
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box p={0}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDatePicker
              value={value}
              onChange={(date) => {
                if (date) onChange(date);
                handleClose();
              }}
              displayStaticWrapperAs="desktop"
              views={getViews(view) as ("year" | "month" | "day")[]}
              openTo={getOpenTo(view)}
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{
                actionBar: { sx: { display: "none" } },
                layout: { sx: { minWidth: 256, maxHeight: 300 } },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </Flex>
  );
};

export default CustomCalendarSelector;
